/**
 * Extract plain text from an uploaded PDF File using `pdfjs-dist`.
 *
 * The library + its worker are dynamically imported so the ~3 MB
 * runtime only ships when the user actually uploads a PDF on the
 * contract-checker /new page.
 */

interface PdfTextItem {
  str: string;
}

export async function extractPdfText(file: File): Promise<string> {
  const [{ getDocument, GlobalWorkerOptions }, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    // Vite resolves `?url` to a hashed asset URL at build time and
    // bundles the worker as a static asset.
    import('pdfjs-dist/build/pdf.worker.mjs?url').then((m) => (m as { default: string }).default),
  ]);

  GlobalWorkerOptions.workerSrc = workerUrl;

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  try {
    const pageTexts: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const text = (content.items as PdfTextItem[])
        .map((item) => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text) pageTexts.push(text);
      page.cleanup();
    }
    return pageTexts.join('\n\n');
  } finally {
    await loadingTask.destroy();
  }
}
